import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import '../styles/features.css';

const Features = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const response = await api.get('/features.php');
      setFeatures(response.data.features || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load features:', err);
      setError('Unable to load features. Please try again later.');
      // Fallback features
      setFeatures([
        { icon: '🛒', title: 'Point of Sale', description: 'Fast, intuitive POS interface with barcode scanning and receipt printing' },
        { icon: '📦', title: 'Inventory Management', description: 'Track stock levels, get low stock alerts, manage suppliers' },
        { icon: '👥', title: 'Customer Management', description: 'Manage customer information, track purchase history, handle credit sales' },
        { icon: '💰', title: 'Debt Tracking', description: 'Sell on credit, track customer balances, record repayments' },
        { icon: '📊', title: 'Smart Reports', description: 'Sales reports, inventory reports, financial analytics' },
        { icon: '📱', title: 'Mobile Ready', description: 'Works on desktop, tablet, and mobile devices' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="features-loading">
        <div className="spinner"></div>
        <p>Loading features...</p>
      </div>
    );
  }

  return (
    <div className="features-page">
      {/* Hero Section */}
      <section className="features-hero">
        <div className="container">
          <h1>Powerful Features for Your Business</h1>
          <p>Everything you need to run your retail business efficiently</p>
        </div>
      </section>

      {error && (
        <div className="features-error">
          <p>{error}</p>
          <button onClick={loadFeatures}>Retry</button>
        </div>
      )}

      {/* Features Grid */}
      <section className="features-grid-section">
        <div className="container">
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Features */}
      <section className="detailed-features">
        <div className="container">
          <h2>Why Choose Harvyq ERP?</h2>
          
          <div className="detailed-feature">
            <div className="feature-content">
              <h3>Fast & Intuitive POS</h3>
              <p>Process sales in seconds with our lightning-fast POS interface. Support for barcode scanners, thermal printers, and cash drawers.</p>
              <ul>
                <li>✓ Barcode scanning</li>
                <li>✓ Multiple payment methods</li>
                <li>✓ Hold/recall sales</li>
                <li>✓ Receipt printing</li>
              </ul>
            </div>
            <div className="feature-image">
              <div className="image-placeholder">🖥️</div>
            </div>
          </div>

          <div className="detailed-feature reverse">
            <div className="feature-content">
              <h3>Smart Inventory Management</h3>
              <p>Never run out of stock with real-time inventory tracking and low stock alerts.</p>
              <ul>
                <li>✓ Real-time stock updates</li>
                <li>✓ Low stock alerts</li>
                <li>✓ Supplier management</li>
                <li>✓ Bulk import/export</li>
              </ul>
            </div>
            <div className="feature-image">
              <div className="image-placeholder">📊</div>
            </div>
          </div>

          <div className="detailed-feature">
            <div className="feature-content">
              <h3>Customer & Debt Management</h3>
              <p>Build customer relationships and manage credit sales efficiently.</p>
              <ul>
                <li>✓ Customer profiles</li>
                <li>✓ Purchase history</li>
                <li>✓ Credit sales tracking</li>
                <li>✓ Automated reminders</li>
              </ul>
            </div>
            <div className="feature-image">
              <div className="image-placeholder">👥</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="features-cta">
        <div className="container">
          <h2>Ready to transform your business?</h2>
          <p>Join hundreds of Kenyan businesses already using Harvyq ERP</p>
          <Link to="/register" className="btn-primary btn-large">
            Start Your Free Trial
          </Link>
          <p className="no-credit-card">14-day free trial, no credit card required</p>
        </div>
      </section>
    </div>
  );
};

export default Features;